import React from 'react';
import { Form, Button } from 'react-bootstrap';

const ConditionalDefaultValueEditor = ({ value, onChange, allFields, fieldOptions = [] }) => {
  const addRule = () => {
    onChange([
      ...(value || []),
      {
        conditions: [{ dependsOnFieldId: '', operator: '==', expectedValue: '' }],
        defaultValue: ''
      }
    ]);
  };

  const removeRule = (ruleIndex) => {
    const updated = [...(value || [])];
    updated.splice(ruleIndex, 1);
    onChange(updated);
  };

  const updateRuleDefaultValue = (ruleIndex, newValue) => {
    const updated = [...(value || [])];
    updated[ruleIndex].defaultValue = newValue;
    onChange(updated);
  };

  const updateCondition = (ruleIndex, condIndex, key, val) => {
    const updated = [...(value || [])];
    updated[ruleIndex].conditions[condIndex][key] = val;
    onChange(updated);
  };

  const addCondition = (ruleIndex) => {
    const updated = [...(value || [])];
    updated[ruleIndex].conditions.push({ dependsOnFieldId: '', operator: '==', expectedValue: '' });
    onChange(updated);
  };

  const removeCondition = (ruleIndex, condIndex) => {
    const updated = [...(value || [])];
    updated[ruleIndex].conditions.splice(condIndex, 1);
    onChange(updated);
  };

  return (
    <>
      <Form.Label className="mt-3">Conditional Default Values</Form.Label>
      <p className="text-muted small">
        Set default values based on other field values. Rules are evaluated in order.
      </p>

      {(value || []).map((rule, ruleIndex) => (
        <div key={ruleIndex} className="border rounded p-3 mb-3 bg-light">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Rule #{ruleIndex + 1}</h6>
            <Button variant="outline-danger" size="sm" onClick={() => removeRule(ruleIndex)}>
              Remove Rule
            </Button>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Default Value</Form.Label>
            <Form.Select
              value={rule.defaultValue || ''}
              onChange={(e) => updateRuleDefaultValue(ruleIndex, e.target.value)}
            >
              <option value="">— Select Default Value —</option>
              {fieldOptions.map((opt, i) => (
                <option key={i} value={opt}>{opt}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Label>Conditions (All must be true)</Form.Label>
          {(rule.conditions || []).map((cond, condIndex) => {
            const depField = allFields.find(f => f.id.toString() === cond.dependsOnFieldId?.toString());

            return (
              <div key={condIndex} className="border p-2 rounded mb-2 bg-white">
                <Form.Group className="mb-2">
                  <Form.Label>When Field</Form.Label>
                  <Form.Select
                    value={cond.dependsOnFieldId || ''}
                    onChange={(e) => updateCondition(ruleIndex, condIndex, 'dependsOnFieldId', e.target.value)}
                  >
                    <option value="">— Select Field —</option>
                    {(allFields || []).map(f => (
                      <option key={f.id} value={f.id}>{f.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>Operator</Form.Label>
                  <Form.Select
                    value={cond.operator || '=='}
                    onChange={(e) => updateCondition(ruleIndex, condIndex, 'operator', e.target.value)}
                  >
                    <option value="==">Equals</option>
                    <option value="!=">Not Equal</option>
                    <option value=">">Greater Than</option>
                    <option value="<">Less Than</option>
                    <option value=">=">≥ Greater or Equal</option>
                    <option value="<=">≤ Less or Equal</option>
                    <option value="contains">Contains</option>
                    <option value="not_contains">Does Not Contain</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>Expected Value</Form.Label>
                  {['dropdown', 'radio'].includes(depField?.type) ? (
                    <Form.Select
                      value={cond.expectedValue || ''}
                      onChange={(e) => updateCondition(ruleIndex, condIndex, 'expectedValue', e.target.value)}
                    >
                      <option value="">-- Select Value --</option>
                      {(depField?.options || []).map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      placeholder="Value"
                      value={cond.expectedValue || ''}
                      onChange={(e) => updateCondition(ruleIndex, condIndex, 'expectedValue', e.target.value)}
                    />
                  )}
                </Form.Group>

                <div className="d-flex justify-content-between">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeCondition(ruleIndex, condIndex)}
                  >
                    × Remove Condition
                  </Button>
                </div>
              </div>
            );
          })}

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => addCondition(ruleIndex)}
          >
            + Add Condition
          </Button>
        </div>
      ))}

      <Button variant="outline-primary" size="sm" onClick={addRule}>
        + Add Conditional Default Rule
      </Button>
    </>
  );
};

export default ConditionalDefaultValueEditor;
